<script setup lang="ts">
import {CallToActionInput, CallToActionSettings} from "@/SocialWidget/types/call_to_action_types";
import {computed, ref, watch} from "vue";
import {emitStatsEvent, isEmpty2} from "@/utils";
import Input from "@/Components/Input.vue";
import Maska from "maska";
import axios from "axios";

const props = defineProps<{
    settings: CallToActionSettings,
    logo?: File | null | undefined,
    bgImage?: File | null | undefined,
    bgLogo?: File | null | undefined,
    endpoint?: string,
    widgetId?: string,
    counters?: any,
}>();

defineEmits(['close']);

const showOk = ref(false);
const obj = ref({});

const update = (key: string, value: any, type: string) => {
    obj.value = {...obj.value, [key]: { title: type, text: value}};
};

function clickButton(type: string) {
    if (type === 'send') {
        created();
    }
    if (type === 'custom') {
        emitStatsEvent('dg_popup_сlick', props.counters);
        fetch(`${props.endpoint}/${props.widgetId}/redirect-custom`);
        window.open(props.settings.button_link,'_blank');
    }
    if (type === 'wa' || type === 'tg') {
        if (type === 'wa') {
            emitStatsEvent('dg_popup_сlick_wa', props.counters);
        }
        if(type === 'tg') {
            emitStatsEvent('dg_popup_сlick_tg', props.counters);
        }
        openMessenger(type);
    }
}
function created() {
    const request = obj.value;
    emitStatsEvent('dg_popup_form', props.counters);
    axios.post(`${props.endpoint}/${props.widgetId}/send-form`, request)
        .then(response => {
            if (response.data === 'OK') {
                showOk.value = true
            }
        });
}

const uploadedLogo = computed(() => {
    if (!isEmpty2(props.logo)) {
        return URL.createObjectURL(props.logo!);
    }
    return '';
});
const uploadedBgImage = computed(() => {
    if (!isEmpty2(props.bgImage)) {
        return URL.createObjectURL(props.bgImage!);
    }
    return '';
});

function openMessenger(type: string) {
    window.open(getMessengerUrl(type),'_blank');
}

function getMessengerUrl(type: string) :string {
    return `${props.endpoint}/${props.widgetId}/redirect?` + [
        `messenger=${type}`,
        `current_url=${encodeURIComponent(window.location.href)}`,
        `referer=${document.referrer}`,
        `device=${getUserDevice()}`,
        `source=modal`,
        `text=`
    ].join('&');
}

function getUserDevice():string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return "tablet";
    }

    if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
        return "mobile";
    }

    return "desktop";
}

const roundStyle = () => {
    if (props.settings.rounded === 'none') {
        return { borderRadius: '0'}
    }
    if (props.settings.rounded === 'standard') {
        return { borderRadius: '8px'}
    }
    if (props.settings.rounded === 'round') {
        return { borderRadius: '24px'}
    }
}
const roundImgStyle = () => {
    if (props.settings.rounded === 'none') {
        return { borderRadius: '0'}
    }
    if (props.settings.rounded === 'standard') {
        return { borderRadius: '6px'}
    }
    if (props.settings.rounded === 'round') {
        return { borderRadius: '20px'}
    }
}
const bgStyle = () => {
    if (props.settings.bg_type === 'full') {
        return {backgroundColor: '#0C1B20'}
    } else {
        if (props.settings.theme === 'dark') {
            return {backgroundColor: '#0C1B20'}
        }
        return {backgroundColor: '#ffffff'}
    }
}
</script>

<template>
    <div class="relative p-[4px] dg-shadow-widget grid grid-cols-8"
         :class="[settings.bg_type === 'without' ? 'max-w-[350px]' : 'max-w-[680px]']"
         :style="[roundStyle(),`color: ${settings.text_color}`, bgStyle()]">
        <button type="button" class="absolute flex items-center p-[8px] rounded-lg right-[16px] top-[16px] z-30" @click="$emit('close')"
                :class="[settings.theme === 'dark' ? 'bg-gray-600 hover:bg-gray-400' : 'bg-gray-100 hover:bg-gray-400',]">
            <svg width="16" height="16" viewBox="0 0 14 13" :fill="settings.text_color" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M11.0833 3.68101L10.2608 2.94788L6.99996 5.85442L3.73913 2.94788L2.91663 3.68101L6.17746 6.58756L2.91663 9.4941L3.73913 10.2272L6.99996 7.32069L10.2608 10.2272L11.0833 9.4941L7.82246 6.58756L11.0833 3.68101Z"
                />
            </svg>
        </button>
        <div class="col-span-4"
             :class="settings.bg_icon_position === 'left' ? 'order-last' : 'order-first'"
             v-if="settings.bg_type === 'full'">
        </div>

        <div class="hidden md:flex md:col-span-4 justify-center items-center brightness-15"
             :class="settings.bg_icon_position === 'left' ? 'order-last' : 'order-first'"
             :style="[roundImgStyle()]"
             v-if="settings.bg_type === 'part'">
            <img v-if="!isEmpty2(props.bgImage) || !settings.bg_image_url"
                 :src="uploadedBgImage"
                 :alt="'gift'"
                 class="object-cover w-full h-full" :style="[roundImgStyle()]">
            <img v-if="settings.bg_image_url && isEmpty2(props.bgImage)"
                 :src="settings.bg_image_url"
                 :alt="'gift'"
                 class="object-cover w-full h-full" :style="[roundImgStyle()]">
        </div>

        <div class="absolute top-0 left-0 w-full h-full z-10 opacity-85 brightness-15"
             v-if="settings.bg_type === 'full'"
             :style="[roundStyle()]">
            <img v-if="!isEmpty2(props.bgImage) || !settings.bg_image_url"
                 :src="uploadedBgImage"
                 class="object-cover w-full h-full rounded-md"
                 :style="[roundStyle()]">
            <img v-if="settings.bg_image_url && isEmpty2(props.bgImage)"
                 :src="settings.bg_image_url"
                 alt="gift"
                 class="object-cover w-full h-full rounded-md"
                 :style="[roundStyle()]">
        </div>

        <div class="px-[16px] py-[24px] z-20"
             :class="[settings.bg_type === 'without' ? 'col-span-full' : 'col-span-4']">
            <div class="flex items-center space-x-2">
                <div v-if="props.settings.logo_enabled">
                    <img v-if="!isEmpty2(props.logo) || !settings.logo_url" :src="uploadedLogo" class="max-h-[32px]">
                    <img v-if="settings.logo_url && isEmpty2(props.logo)" :src="settings.logo_url" alt="site" class="max-h-[32px]">
                </div>
                <span v-if="!isEmpty2(props.settings.site_name)" class="text-[16px]">{{settings.site_name}}</span>
            </div>
            <div class="mt-5" v-if="!showOk">
                <h3 class="font-bold mb-[12px] text-[18px]">
                    {{ settings.title }}
                </h3>

                <p class="mb-[16px] text-[14px] opacity-85 whitespace-pre-wrap">
                    {{ settings.description }}
                </p>
                <form v-if="settings.inputs?.length"
                     class="space-y-3 mt-[24px]" @submit.prevent="created()">
                    <template v-for="item in settings.inputs">
                        <label class="block font-semibold text-[14px]">
                            {{item.title}}
                            <span v-if="item.required" :style="[`color: ${settings.button_color}`]">*</span>
                        </label>
                        <input
                            @input="update(item.id, ($event.target as HTMLInputElement).value, item.title)"
                            class="w-full bg-transparent rounded-md"
                            v-if="item.type === 'name'"
                            type="text"
                            :required="item.required"
                            :placeholder="item.placeholder">
                        <input
                            @input="update(item.id, ($event.target as HTMLInputElement).value, item.title)"
                            class="w-full bg-transparent rounded-md"
                            v-if="item.type === 'email'"
                            type="email"
                            :required="item.required"
                            placeholder="еxample@email.ru"
                            autocomplete="email">
                        <input
                            @input="update(item.id, ($event.target as HTMLInputElement).value, item.title)"
                            class="w-full bg-transparent rounded-md"
                            v-if="item.type === 'phone'"
                            v type="tel"
                            :required="item.required"
                            v-maska="'+# (###) ###-##-##'"
                            placeholder="+7 (999) 999-99-99">
                    </template>
                    <button type="submit" class="font-semibold text-[16px] px-[32px] py-[8px] mt-[24px]"
                            :style="[roundImgStyle(),`color: ${settings.button_text_color}`, `background: ${settings.button_color}`]">
                        {{settings.button_text}}
                    </button>
                </form>
                <button type="button"
                        v-if="settings.button_type != 'send'"
                        @click="clickButton(settings.button_type)"
                        class="font-semibold text-[16px] px-[32px] py-[8px] mt-[24px]"
                        :style="[roundImgStyle(),`color: ${settings.button_text_color}`, `background: ${settings.button_color}`]">
                    {{settings.button_text}}
                </button>
                <p class="text-[12px] opacity-85 mt-[8px]" v-if="settings.inputs?.length">Нажимая кнопку, вы соглашаетесь с
                    <a :href="settings.policy_link ?? '#'"
                       target="_blank"
                       :style="[`color: ${settings.button_color}`]">политикой конфиденциальности</a>
                </p>
                <p v-if="!settings.disable_copyright" class="text-[12px] mt-[24px]"><span class="opacity-75">Сделано в </span>
                    <a href="https://dailygrow.ru/" class="opacity-100"
                       target="_blank">DailyGrow</a>
                </p>

            </div>
            <div class="mt-[20px]" v-if="showOk">
                <h3 class="font-bold mb-[12px] text-[18px] whitespace-pre-wrap">
                    {{settings.success_send_text}}
                </h3>
            </div>
        </div>

    </div>


</template>

<style scoped>

</style>
